import { Suspense, lazy, useEffect, useRef } from 'react';
import { avatarEnabled } from '../lib/avatarFlag';
import { BRAND } from '../lib/brand';
import { type ComposerDraftField, createComposerDraftEmitter } from '../lib/composerDraft';
import { deriveOrbState } from '../lib/orbState';
import { useVoice } from '../lib/useVoice';
import { useDemoStore } from '../store';
import { ControlBar } from './ControlBar';
import { GapNoteToast } from './GapNoteToast';
import { Icon } from './Icon';
import { ShowcaseScene } from './ShowcaseScene';
import { ThemeRoot } from './ThemeRoot';
import { ThemeToggle } from './ThemeToggle';
import { Transcript } from './Transcript';
import { type ComposerHandle, TranscriptComposer } from './TranscriptComposer';
import { TryAsking } from './TryAsking';
import { VoiceOrb } from './VoiceOrb';
import { type GovernanceEvent, SessionGovernanceOverlay } from './kiosk/SessionGovernanceOverlay';
import { TimeRemainingCue } from './kiosk/TimeRemainingCue';

/**
 * R31 avatar Phase-1 — the 3D avatar for the sticky-top slot, behind the `?avatar=1` flag.
 * `lazy()` so Three.js/TalkingHead (the ~heavy chunk) are imported ONLY when the flag is on; the
 * initial bundle is unchanged when the flag is absent. Evaluated once at module scope, matching the
 * `?audiodebug` inert-in-production precedent. When off, `AVATAR_ON` is false and the VoiceOrb path
 * is byte-for-byte today's.
 */
const AVATAR_ON = avatarEnabled();
const Avatar3D = AVATAR_ON ? lazy(() => import('./Avatar3D')) : null;

/**
 * Booth kiosk live page (frontend-spec §2). Panels: controls (connect / hang up / mute), the
 * voice-synced animation (VoiceOrb), the live transcript, and the product showcase (cards the agent
 * pushes over the WS). Voice transport = Pipecat over WebSocket (useVoice / ADR-002).
 *
 * Round 4/5: now also renders:
 *  - SessionGovernanceOverlay (still-there / time-warning / quality-guard banners) driven from
 *    GovernanceEvent WS app-messages (conversation-design.md §6d).
 *    Round 5: `ui.governance` WS messages are now wired in useVoice.ts — Butler emits the
 *    governance contract shape and it flows through to the overlay with no UI changes needed.
 *  - TimeRemainingCue (thin progress bar, last 120s) driven from the same governance event source.
 */
export function VoiceDemo() {
  const {
    disconnect,
    startWarming,
    toggleMic,
    pauseToggle,
    sendUserText,
    // R30 V1 — the live composer-draft emit (§40.4b client half); debounced at this call site.
    sendComposerDraft,
    selectLanguage,
    uploadCard,
    prefillLead,
    sendBrowsedCategory,
    sendTapToSelect,
    sendTapToDeselect,
    sendContactForm,
    // R10 — showcase synergy dispatch (additive; all no-op when disconnected).
    sendContactSubmitted,
    sendGridLayout,
    sendOrderConfirmed,
    sendOrderReviewUpdated,
    sendCloseDone,
    sendStyleFeedback,
    sendStyleChipsUpdated,
    sendShortlistUpdate,
    // R25 — card-scan surface gating (emit card-modal + local mic belt via the established mute path).
    setScanSurfaceActive,
    // R18 client-audio fix: ref-based audio levels, no per-frame store write.
    // The orb reads this ref directly in its own rAF loop.
    levelRef,
  } = useVoice();
  const status = useDemoStore((s) => s.status);
  const error = useDemoStore((s) => s.error);
  const micEnabled = useDemoStore((s) => s.micEnabled);
  const paused = useDemoStore((s) => s.paused);
  const language = useDemoStore((s) => s.language);
  const cards = useDemoStore((s) => s.cards);
  // Round 5: governance events now wired — useVoice handles ui.governance WS messages.
  const governanceEvent = useDemoStore((s) => s.governanceEvent);
  const secondsLeft = useDemoStore((s) => s.secondsLeft);
  const clearGovernanceEvent = useDemoStore((s) => s.clearGovernanceEvent);
  const selectedSkus = useDemoStore((s) => s.selectedSkus);
  const setSelectedSkus = useDemoStore((s) => s.setSelectedSkus);
  const ackedSelections = useDemoStore((s) => s.ackedSelections);
  const conversationId = useDemoStore((s) => s.conversationId);
  const scene = useDemoStore((s) => s.scene);
  const setScene = useDemoStore((s) => s.setScene);
  const sessionRecording = useDemoStore((s) => s.sessionRecording);
  // R11 #6 — knowledge-gap "noted" overlay chip on the showcase (service-not-failure).
  const gapNote = useDemoStore((s) => s.gapNote);
  const setGapNote = useDemoStore((s) => s.setGapNote);
  const connected = status === 'connected';

  // R29 X4 — the voice-orb choreography state (design-language §6), derived from state the client
  // ALREADY has via a selector that returns the DERIVED STRING, so the orb re-renders only when the
  // state actually changes (≈once per turn) — never per transcript chunk / audio frame (R18 constraint,
  // no per-frame orb reads). Inputs: status + paused + the last transcript line's speaker/final flag.
  const orbState = useDemoStore((s) => {
    const last = s.transcript.at(-1);
    return deriveOrbState({
      status: s.status,
      paused: s.paused,
      lastSpeaker: last ? (last.speaker === 'agent' ? 'agent' : 'visitor') : null,
      lastFinal: last?.final ?? true,
    });
  });

  /**
   * R14 B5 → R23 C1 — the chat composer is now ALWAYS VISIBLE (USER-SPECIFIED), anchored at the bottom
   * of the transcript region (chat convention), not toggled. Pressing Type in the control bar no longer
   * opens/closes it — it FOCUS/SCROLL-TO's the field via this imperative handle (and stays the §31 pulse
   * target). Three field sessions proved open-on-demand failed ("I don't see a box").
   */
  const composerRef = useRef<ComposerHandle | null>(null);

  /**
   * R30 V1 — the shared DEBOUNCED composer-draft emitter (§40.4b client half). ONE emitter coalesces
   * keystrokes from BOTH the S7 close-card fields (email/phone) and the always-open Type composer, so
   * the wire never floods and a field switch flushes to the newest field only. The emit fn is stable
   * (`sendComposerDraft` from useVoice); kept in a ref so the emitter survives re-renders. Disposed on
   * unmount so no orphaned draft fires after the page tears down.
   */
  const draftEmitterRef = useRef<ReturnType<typeof createComposerDraftEmitter> | null>(null);
  if (draftEmitterRef.current === null) {
    draftEmitterRef.current = createComposerDraftEmitter((field, value) =>
      sendComposerDraft(field, value)
    );
  }
  useEffect(() => {
    const emitter = draftEmitterRef.current;
    return () => emitter?.dispose();
  }, []);
  const onDraftChange = (field: ComposerDraftField, value: string) =>
    draftEmitterRef.current?.change(field, value);
  const onDraftStop = () => draftEmitterRef.current?.stop();

  /*
   * R14 B1 — the interactive style-feedback walkthrough now lives IN-CALL, inside the S9 end scene
   * (ShowcaseScene → EndScene), not after hang-up. The former post-hangup StyleSnapshotWalkthrough
   * trigger (status 'connected' → 'idle') is removed; the server disables its side (R31). The
   * StyleSnapshotWalkthrough component is retained for reuse but is no longer mounted here.
   */

  return (
    <ThemeRoot
      surface="live"
      className="h-screen bg-canvas text-ink flex flex-col overflow-hidden font-body"
    >
      {/*
       * TimeRemainingCue: thin progress bar fixed to top, visible in last 120s.
       * secondsLeft comes from the store (governance adapter below).
       */}
      <TimeRemainingCue secondsLeft={secondsLeft ?? null} />

      <header className="px-6 py-4 sm:px-8 border-b hairline shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-3">
            <h1 className="font-display text-2xl leading-none tracking-tight text-ink">
              {BRAND.heading}
            </h1>
            <span aria-hidden className="hidden h-4 w-px bg-line-strong sm:inline-block" />
            <p className="hidden text-sm text-subtle sm:block">{BRAND.tagline}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden items-center gap-1.5 rounded-full bg-fill ring-1 ring-line px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-subtle sm:inline-flex">
              Booth concierge
            </span>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Split screen: showcase (½) + conversation (½). Stacks on small screens. */}
      <main className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2">
        {/* LEFT — product showcase + selection. `relative` anchors the R11 gap-noted overlay chip. */}
        <section className="relative min-h-0 overflow-y-auto border-b md:border-b-0 md:border-r hairline p-6 sm:p-8">
          <h2 className="font-display text-xs uppercase tracking-[0.2em] text-subtle mb-5">
            Showcase
          </h2>
          {/*
           * R14 B1 — the ShowcaseScene owns every scene including the S9 end scene, whose in-call
           * style-feedback walkthrough replaces the former post-hangup StyleSnapshotWalkthrough. The
           * showcase is always the stage here (no post-hangup swap).
           */}
          <ShowcaseScene
            cards={cards}
            selectedSkus={selectedSkus}
            onSelectionChange={setSelectedSkus}
            connected={connected}
            conversationId={conversationId}
            onBrowseCategory={sendBrowsedCategory}
            onTapToSelect={sendTapToSelect}
            onTapToDeselect={sendTapToDeselect}
            onGridLayout={sendGridLayout}
            onShortlistUpdate={sendShortlistUpdate}
            onContactSubmitted={sendContactSubmitted}
            onOrderConfirmed={sendOrderConfirmed}
            // R16 V1 — live review mirror (contract §14): seed + every stepper/removal + pre-confirm.
            onReviewUpdated={sendOrderReviewUpdated}
            // Close-card email reuses the existing submit-contact path (server merges the lead).
            onCloseEmail={sendContactForm}
            // R13 U1: Done → server closing beat (ui.scene:end) + local advance to the S9 end scene.
            onCloseDone={sendCloseDone}
            // R14 B1: Confirm on the end-scene feedback → style-feedback {kept, removed, added}.
            onStyleFeedback={sendStyleFeedback}
            // R17 Q3 (R34 §16): every chip toggle + the initial set → style-chips-updated {selected}
            // (debounced in EndScene) so Gigi can discuss the CHIPS accurately before the Confirm.
            onStyleChipsUpdated={sendStyleChipsUpdated}
            // R13 U1: "start a new conversation" → full hang-up/reset for the next visitor.
            onNewConversation={() => void disconnect()}
            // R25 — S1 welcome + S7 close card scans gate the mic + emit the §38 `card-modal` signal.
            onScanSurfaceChange={setScanSurfaceActive}
            // R30 V1 — the S7 close-card typing emit (§40.4b `composer-draft`), debounced by the shared
            // emitter above; holds the server's send/wind-down timeout while the visitor types.
            onDraftChange={(field, value) => onDraftChange(field, value)}
            onDraftStop={onDraftStop}
          />
          {/*
           * R11 #6 — knowledge-gap "noted" overlay chip (service-not-failure). Floats over the
           * bottom of the showcase; auto-dismisses. Never a scene change — the active scene keeps
           * the stage underneath. Driven by ui.gap_noted (useVoice) → gapNote in the store.
           */}
          {gapNote && <GapNoteToast note={gapNote} onDismiss={() => setGapNote(null)} />}
        </section>

        {/*
         * RIGHT — voice + live conversation. R14 B4: two regions —
         *   1. STICKY top: the orb + selection tray + control bar (unmute/pause/type/language/card/
         *      hang-up) + status. Never scrolls away mid-session (shrink-0, no overflow).
         *   2. SCROLLABLE below: the transcript, with the R14 B5 chat composer anchored at its BOTTOM.
         * The section itself no longer scrolls — its scrollable child owns the overflow, so the
         * controls stay put while the dialogue scrolls under them.
         */}
        <section className="min-h-0 flex flex-col p-6 sm:p-8 gap-4">
          {/* ── STICKY region: orb + tray + controls + status ── */}
          <div className="shrink-0 flex flex-col items-center gap-4">
            {/* R18 client-audio: levelRef drives amplitude imperatively (rAF loop) — no re-render per frame.
             * R31: behind `?avatar=1`, the 3D avatar takes this exact slot (Avatar3D falls back to the
             * orb internally for reduced-motion / no-WebGL / GLB-load — and while the GLB streams in via
             * the Suspense fallback). Flag off ⇒ Avatar3D is null and this is byte-for-byte today's orb. */}
            {Avatar3D ? (
              <Suspense
                fallback={<VoiceOrb status={status} orbState={orbState} levelRef={levelRef} />}
              >
                <Avatar3D status={status} orbState={orbState} levelRef={levelRef} />
              </Suspense>
            ) : (
              <VoiceOrb status={status} orbState={orbState} levelRef={levelRef} />
            )}
            {/*
             * Selection tray — R18 Wave 1.5 P1:
             * Driven by ackedSelections (server-confirmed via ui.selection_ack) rather than
             * purely optimistic local state. This means the tray reflects what the server knows
             * was tapped, which is what Gigi uses for place_order backfill. The two sets
             * converge in <1s (ack is best-effort: if no ack arrives, ackedSelections stays empty
             * while selectedSkus still shows the local card highlight — visual state is fine either way).
             */}
            {Object.keys(ackedSelections).length > 0 && (
              <div className="flex w-full max-w-xl items-center gap-2 flex-wrap">
                <span className="text-xs text-subtle shrink-0">
                  {Object.keys(ackedSelections).length} confirmed:
                </span>
                {Object.entries(ackedSelections).map(([sku, name]) => (
                  <span
                    key={sku}
                    className="inline-flex items-center gap-1 rounded-full bg-brass-soft text-brass text-[11px] px-2.5 py-1 ring-1 ring-brass/30"
                  >
                    {name || sku}
                    <button
                      type="button"
                      aria-label={`Remove ${name || sku}`}
                      onClick={() => {
                        // Remove from local state and emit deselect WS message
                        setSelectedSkus(selectedSkus.filter((s) => s !== sku));
                        sendTapToDeselect(sku, name || sku);
                      }}
                      className="ml-0.5 hover:text-ink"
                    >
                      <Icon name="x" size={10} />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    // Clear locally and emit deselect for each server-acked SKU
                    for (const [sku, name] of Object.entries(ackedSelections)) {
                      sendTapToDeselect(sku, name || sku);
                    }
                    setSelectedSkus([]);
                  }}
                  className="text-[11px] text-faint hover:text-subtle ml-1"
                >
                  Clear all
                </button>
              </div>
            )}
            {connected ? (
              <ControlBar
                micEnabled={micEnabled}
                paused={paused}
                language={language}
                onMute={toggleMic}
                onPause={pauseToggle}
                // R23 C1: Type focuses the always-visible composer anchored at the bottom of the
                // transcript region (below) — it no longer opens/closes anything.
                onFocusComposer={() => composerRef.current?.focus()}
                onSelectLanguage={selectLanguage}
                onUpload={uploadCard}
                onPrefillLead={prefillLead}
                onHangUp={() => void disconnect()}
                // R25 — gate the mic + emit the §38 `card-modal` signal while a scan surface is open.
                onScanSurfaceChange={setScanSurfaceActive}
              />
            ) : status === 'reconnecting' ? (
              /* R18 Wave 1.7-C2 — auto-reconnect in progress (bounded retry, same uuid).
               * Show a small indicator with a Start over escape hatch. The dropped panel
               * only appears once all bounded retries are exhausted. */
              <ReconnectingIndicator onStartOver={() => void disconnect()} />
            ) : status === 'dropped' ? (
              /* WS dropped unexpectedly — show reconnect / start-over (Bug 1 fix — round-8-voice).
               * Reconnect now goes through the warm-up handshake (new uuid) rather than direct WS. */
              <DroppedReconnectPanel
                onReconnect={() => void startWarming()}
                onStartOver={() => void disconnect()}
              />
            ) : status === 'warming' ? (
              /* R18 Wave 1.6 — warm-up in progress. Panels (showcase, transcript) load in background.
               * WarmingScreen shown in the orb area; the panels are already mounted above. */
              <WarmingScreen onStartOver={() => void disconnect()} />
            ) : status === 'warmup-timeout' ? (
              /* R18 Wave 1.6 — warm-up budget exceeded. Graceful retry affordance. */
              <WarmupTimeoutPanel
                onRetry={() => void startWarming()}
                onStartOver={() => void disconnect()}
              />
            ) : (
              <TalkButton
                status={status}
                micEnabled={micEnabled}
                onConnect={() => void startWarming()}
                onToggleMic={toggleMic}
              />
            )}
            <StatusLine status={status} error={error} micEnabled={micEnabled} />

            {/*
             * R10 — structured capture moved OFF the transcript and onto the showcase (S1/S6/S7).
             * The only affordance kept here is a touch path to the order-review scene so a visitor
             * with a selection can reach S6 without waiting for Gigi to offer it — touch is a full
             * peer to voice. Kept in the sticky region so it's always reachable.
             */}
            {selectedSkus.length > 0 && scene !== 'order_review' && (
              <button
                type="button"
                data-testid="review-order-btn"
                onClick={() => setScene('order_review')}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm text-brass ring-1 ring-brass/40 hover:bg-brass-soft transition-colors"
              >
                <Icon name="package" size={14} />
                Review my selection ({selectedSkus.length})
              </button>
            )}
            {status === 'idle' && <TryAsking />}
          </div>

          {/*
           * ── SCROLLABLE region: the transcript + the R23 C1 ALWAYS-VISIBLE chat composer anchored at
           * its bottom. The transcript is the ONLY scrolling element (min-h-0 + overflow-y-auto), so the
           * sticky orb + control bar above never scroll away. The composer sits below the transcript
           * (chat convention, USER-SPECIFIED placement — below the transcript, NOT above the control
           * bar); it's a shrink-0 member of the flex column so it stays pinned at the region's foot and
           * never overlaps the showcase. It's passive at rest and expands on focus (see the component).
           */}
          {connected && (
            <div className="min-h-0 flex-1 flex flex-col w-full max-w-xl mx-auto gap-3">
              {/* R27 — edge-fade on the scroll region: the top/bottom of the transcript dissolve into
                  the panel (mask-image) rather than hard-clipping, so scrolled-off turns feel gallery-
                  soft. Purely cosmetic; the region still scrolls normally. */}
              <div
                className="min-h-0 flex-1 overflow-y-auto"
                style={{
                  maskImage:
                    'linear-gradient(to bottom, transparent 0, #000 16px, #000 calc(100% - 16px), transparent 100%)',
                  WebkitMaskImage:
                    'linear-gradient(to bottom, transparent 0, #000 16px, #000 calc(100% - 16px), transparent 100%)',
                }}
              >
                <Transcript />
              </div>
              <TranscriptComposer
                ref={composerRef}
                onSend={(t) => sendUserText(t)}
                micEnabled={micEnabled}
                // R30 V1 — the Type composer also emits the debounced §40.4b draft (field 'composer'),
                // via the SHARED emitter, so an out-of-flow email/phone typed here holds the timeout too.
                onDraftChange={(value) => onDraftChange('composer', value)}
                onDraftStop={onDraftStop}
              />
            </div>
          )}
        </section>
      </main>

      {/* R28 (3.5) — the disclosure line anchored into a slim quiet bar: a faint tinted fill under a
          hairline so it reads as a deliberate footer rail (not a loose line floating on the canvas).
          The build tag stays right-aligned. */}
      <footer className="bg-fill/60 px-6 py-2.5 sm:px-8 border-t hairline shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-subtle">
          <span className="text-ink/70">{BRAND.consent_short}</span>
          {connected && sessionRecording && (
            // ⚑ ADAPTER: awaits Butler ui.session_config WS message (docs/voice-capture-contract.md)
            <span
              data-testid="recording-disclosure"
              className="inline-flex items-center gap-1.5 text-[11px] text-subtle px-2 py-0.5 rounded-full ring-1 ring-line"
            >
              <span className="w-2 h-2 rounded-full bg-terracotta animate-pulse" aria-hidden />
              Recorded for quality
            </span>
          )}
          <span className="text-faint">build {import.meta.env.VITE_BUILD_ID ?? 'dev'}</span>
        </div>
      </footer>

      {/*
       * SessionGovernanceOverlay: bottom-anchored toast for still-there / time-warning / quality-guard.
       * Gated on `connected` ONLY (Bug 1 fix — round-8-voice): the overlay must NOT show after a
       * WS drop/crash ('dropped', 'idle', 'error') — it only fires in a live connected session.
       * Driven from governanceEvent in the store (adapter: Butler emits ui.governance WS messages).
       * Actions: onAcknowledge dismisses the overlay; onHangUp calls disconnect().
       */}
      {connected && (
        <GovernanceOverlayBridge
          governanceEvent={governanceEvent}
          onClear={clearGovernanceEvent}
          onDisconnect={() => void disconnect()}
        />
      )}
    </ThemeRoot>
  );
}

/**
 * R18 Wave 1.6 — Warm-up loading screen (status === 'warming').
 * Shown while the container boots (~20-35s from cold). The panels (Showcase, Transcript)
 * continue to mount in the background so they're ready when the conversation begins.
 * The VoiceOrb above this renders in 'idle' mode (breathing, not connected) — gracious, not
 * technical. The "Start over" escape is always visible.
 */
function WarmingScreen({ onStartOver }: { onStartOver: () => void }) {
  return (
    <div className="reveal flex flex-col items-center gap-5 text-center">
      <div className="flex flex-col items-center gap-2">
        <p className="font-display text-lg text-ink tracking-tight">Warming up Gigi…</p>
        <p className="text-sm text-subtle max-w-xs leading-relaxed">
          Just a moment — preparing your personal concierge.
        </p>
      </div>

      {/* Animated progress dots — three brass dots pulsing in sequence */}
      <WarmingDots />

      <button
        type="button"
        onClick={onStartOver}
        className="mt-1 text-xs text-faint hover:text-subtle underline underline-offset-2 transition-colors duration-1 ease-physical min-h-[44px] flex items-center"
        aria-label="Cancel and return to the start screen"
      >
        Start over
      </button>
    </div>
  );
}

/**
 * Three brass dots that pulse in left-to-right sequence, indicating active loading.
 * Reduced-motion: still, golden dots (no animation) via the @media clause in index.css.
 */
function WarmingDots() {
  return (
    <output className="flex items-center gap-2" aria-label="Loading" aria-live="polite">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          aria-hidden
          style={{
            display: 'inline-block',
            width: '7px',
            height: '7px',
            borderRadius: '50%',
            background: 'var(--c-accent)',
            opacity: 0.9,
            animation: `warming-dot 1.4s var(--ease-out) ${i * 0.22}s infinite`,
          }}
        />
      ))}
    </output>
  );
}

/**
 * R18 Wave 1.6 — Warm-up timeout panel (status === 'warmup-timeout').
 * Shown when the container hasn't answered within the ~55s budget. Gracious, not technical.
 * Retry re-runs the full warm-up flow with a fresh uuid.
 */
function WarmupTimeoutPanel({
  onRetry,
  onStartOver,
}: {
  onRetry: () => void;
  onStartOver: () => void;
}) {
  return (
    <div className="reveal flex flex-col items-center gap-4 text-center">
      <div className="flex flex-col items-center gap-1">
        <p className="font-display text-base text-ink">Taking longer than usual</p>
        <p className="text-sm text-subtle max-w-xs leading-relaxed">
          The concierge is still warming up. Please try again in a moment.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="btn-primary inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-display shadow-soft"
        >
          <Icon name="refresh" size={16} />
          Retry
        </button>
        <button
          type="button"
          onClick={onStartOver}
          className="rounded-full px-6 py-4 text-sm text-subtle ring-1 ring-line hover:ring-line-strong hover:text-ink transition-colors"
        >
          Start over
        </button>
      </div>
    </div>
  );
}

/**
 * Shown during R18 Wave 1.7-C2 auto-reconnect (status === 'reconnecting').
 * The bounded retry loop is running (up to 3 attempts, 1s/2s/4s backoff, same uuid).
 * Shows a small animated indicator and a "Start over" escape hatch so the visitor can
 * abort the retry and reset to the idle screen if they prefer.
 */
function ReconnectingIndicator({ onStartOver }: { onStartOver: () => void }) {
  return (
    <div
      data-testid="reconnecting-indicator"
      className="flex flex-col items-center gap-4 text-center"
    >
      <div className="flex items-center gap-2 text-sm text-subtle">
        <span className="w-2 h-2 rounded-full bg-brass animate-pulse" aria-hidden />
        Reconnecting…
      </div>
      <button
        type="button"
        onClick={onStartOver}
        className="rounded-full px-6 py-3 text-sm text-subtle ring-1 ring-line hover:ring-line-strong hover:text-ink transition-colors"
      >
        Start over
      </button>
    </div>
  );
}

/**
 * Shown when the WS dropped unexpectedly mid-session (status === 'dropped').
 * Bug 1 fix — round-8-voice: replaces the zombie voice UI so the visitor/staff have a clear
 * recovery path rather than a frozen screen with no affordance.
 * - "Reconnect" goes through the warm-up handshake (new uuid) so the reconnect WS also hits
 *   a warm container (R18 Wave 1.6).
 * - "Start over" calls the full disconnect/reset to return to the idle home screen.
 */
function DroppedReconnectPanel({
  onReconnect,
  onStartOver,
}: {
  onReconnect: () => void;
  onStartOver: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <p className="text-sm text-subtle max-w-xs leading-relaxed">
        Connection dropped. Tap to reconnect or start a fresh session.
      </p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onReconnect}
          className="btn-primary inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-display shadow-soft"
        >
          <Icon name="play" size={16} />
          Reconnect
        </button>
        <button
          type="button"
          onClick={onStartOver}
          className="rounded-full px-6 py-4 text-sm text-subtle ring-1 ring-line hover:ring-line-strong hover:text-ink transition-colors"
        >
          Start over
        </button>
      </div>
    </div>
  );
}

/**
 * Bridges the serialisable StoredGovernanceEvent (from the Zustand store, no callbacks) into
 * a full GovernanceEvent (with callbacks) that SessionGovernanceOverlay understands.
 * Separated into its own component so JSX multi-statement handlers don't cause parse confusion.
 */
function GovernanceOverlayBridge({
  governanceEvent,
  onClear,
  onDisconnect,
}: {
  governanceEvent: import('../store').StoredGovernanceEvent | null;
  onClear: () => void;
  onDisconnect: () => void;
}) {
  let resolved: GovernanceEvent | null = null;
  if (governanceEvent?.type === 'still-there') {
    resolved = {
      type: 'still-there',
      onAcknowledge: onClear,
      onHangUp: () => {
        onClear();
        onDisconnect();
      },
    };
  } else if (governanceEvent?.type === 'time-warning') {
    resolved = {
      type: 'time-warning',
      secondsLeft: governanceEvent.secondsLeft,
      onExtend: governanceEvent.canExtend ? onClear : undefined,
      onHangUp: () => {
        onClear();
        onDisconnect();
      },
    };
  } else if (governanceEvent?.type === 'quality-guard') {
    resolved = {
      type: 'quality-guard',
      suggestion: governanceEvent.suggestion,
      onAcknowledge: onClear,
    };
  }
  return <SessionGovernanceOverlay event={resolved} />;
}

function TalkButton({
  status,
  micEnabled,
  onConnect,
  onToggleMic,
}: {
  status: string;
  micEnabled: boolean;
  onConnect: () => void;
  onToggleMic: () => void;
}) {
  // 'warming' is handled by WarmingScreen above; TalkButton only renders in idle/error states.
  const connecting = status === 'connecting';
  const connected = status === 'connected';
  const label = connected
    ? micEnabled
      ? (BRAND.listening_label ?? 'Listening… tap to mute')
      : 'Muted — tap to talk'
    : connecting
      ? 'Connecting…'
      : (BRAND.start_label ?? 'Start talking');

  // R28 (3.1) — the pre-connect "Start talking" tap is the floor's HERO action: larger, orb-adjacent
  // (it renders directly beneath the VoiceOrb in the sticky column), and it BREATHES a slow brass
  // halo (`.hero-tap`, Wave-1 elev-lift + brass-soft) so a passer-by's eye lands on the one door.
  // Once connected the button reverts to the quieter listening/mute affordance (no hero halo — the
  // session is already underway). Reduced-motion → a steady lifted ring (the .hero-tap fallback).
  const isHero = !connected && !connecting;
  return (
    <button
      type="button"
      disabled={connecting}
      onClick={() => (connected ? onToggleMic() : onConnect())}
      aria-label={label}
      className={`inline-flex items-center gap-2.5 rounded-full font-display transition-transform duration-2 ease-physical hover:scale-[1.02] active:scale-[0.99] disabled:opacity-60 ${
        isHero ? 'hero-tap px-12 py-7 text-xl' : 'shadow-soft px-10 py-6 text-lg'
      } ${connected && micEnabled ? 'btn-accent' : 'btn-primary'}`}
    >
      <Icon name={connected && micEnabled ? 'mic' : 'play'} size={isHero ? 24 : 20} />
      {label}
    </button>
  );
}

function StatusLine({
  status,
  error,
  micEnabled,
}: { status: string; error?: string; micEnabled: boolean }) {
  if (status === 'error') {
    return (
      <p className="text-sm text-terracotta">
        Couldn't connect: {error ?? 'unknown error'}. Tap to retry.
      </p>
    );
  }
  if (
    status === 'dropped' ||
    status === 'warming' ||
    status === 'warmup-timeout' ||
    status === 'reconnecting'
  ) {
    // ReconnectingIndicator / WarmingScreen / WarmupTimeoutPanel / DroppedReconnectPanel render their own copy above.
    return null;
  }
  if (status === 'connected') {
    // R27 — the "Speak naturally…" invitation is folded into the composer placeholder (it floated
    // loose here before). While the mic is live we stay quiet; when muted we keep a small indicator
    // so the visitor knows why Gigi isn't hearing them (the composer placeholder echoes it too).
    if (micEnabled) return null;
    return <p className="text-sm text-subtle">Microphone muted — type below to reach Gigi.</p>;
  }
  if (status === 'idle') {
    return (
      <p className="text-sm text-subtle">
        Tap to start — you'll talk, I'll listen and show pieces on screen.
      </p>
    );
  }
  return null;
}
